import { Link, usePage } from '@inertiajs/react'
import { ReactNode, useState } from 'react'
import NotificationBell from '../components/NotificationBell'

interface Account {
  id: number
  name: string
  slug: string
}

interface NavItem {
  href: string
  label: string
}

interface LayoutProps {
  children: ReactNode
  account?: Account
  permissions?: {
    can_view_dashboard: boolean
    can_manage_products: boolean
    can_use_pos: boolean
    can_manage_customers: boolean
    can_manage_categories: boolean
    can_manage_account: boolean
    can_manage_branches: boolean
    can_manage_orders: boolean
    can_manage_sales: boolean
    can_view_reports: boolean
    can_manage_users: boolean
    can_manage_devices: boolean
  }
  /** When true, the content area spans the full viewport width instead of
   *  being constrained to max-w-7xl (used by the POS page). */
  fullWidth?: boolean
}

interface PageProps {
  flash?: {
    notice?: string
    alert?: string
  }
  super_admin_visiting_slug?: string
  super_admin_visiting_account_name?: string
  unread_notifications_count?: number
  [key: string]: unknown
}

export default function Layout({ children, account, permissions: propPermissions, fullWidth }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { props: pageProps } = usePage<PageProps>()
  const { flash, super_admin_visiting_slug, super_admin_visiting_account_name, permissions: sharedPermissions, unread_notifications_count } = pageProps

  const defaultPermissions = {
    can_view_dashboard: true,
    can_manage_products: true,
    can_use_pos: true,
    can_manage_customers: true,
    can_manage_categories: true,
    can_manage_account: false,
    can_manage_branches: true,
    can_manage_orders: true,
    can_manage_sales: true,
    can_view_reports: true,
    can_manage_users: false,
    can_manage_devices: false,
  }

  // Usar permisos compartidos del backend, luego prop, luego defaults
  const perms = { ...defaultPermissions, ...propPermissions, ...sharedPermissions }

  const navItems: NavItem[] = []

  if (account) {
    if (perms.can_view_dashboard) {
      navItems.push({ href: `/${account.slug}/dashboard`, label: 'Dashboard' })
    }
    if (perms.can_manage_products) {
      navItems.push({ href: `/${account.slug}/products`, label: 'Productos' })
      navItems.push({ href: `/${account.slug}/promotions`, label: 'Promociones' })
    }
    if (perms.can_use_pos) {
      navItems.push({ href: `/${account.slug}/pos`, label: 'POS' })
    }
    if (perms.can_manage_branches) {
      navItems.push({ href: `/${account.slug}/branches`, label: 'Sucursales' })
    }
    if (perms.can_manage_customers) {
      navItems.push({ href: `/${account.slug}/customers`, label: 'Clientes' })
    }
    if (perms.can_manage_categories) {
      navItems.push({ href: `/${account.slug}/categories`, label: 'Categorías' })
    }
    if (perms.can_manage_orders) {
      navItems.push({ href: `/${account.slug}/orders`, label: 'Pedidos Web' })
    }
    if (perms.can_manage_sales) {
      navItems.push({ href: `/${account.slug}/sales`, label: 'Ventas' })
    }
    if (perms.can_view_reports) {
      navItems.push({ href: `/${account.slug}/reports`, label: 'Reportes' })
    }
    if (perms.can_manage_users) {
      navItems.push({ href: `/${account.slug}/users`, label: 'Usuarios' })
    }
    if (perms.can_manage_devices) {
      navItems.push({ href: `/${account.slug}/devices`, label: 'Impresoras' })
    }
    if (perms.can_manage_account) {
      navItems.push({ href: `/${account.slug}/settings/account`, label: 'Configuración' })
    }
    if ((pageProps as any).tenant_type === 'restaurante') {
      navItems.push({ href: `/${account.slug}/kitchen`, label: 'Comandas' })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner de modo Super Admin visitando tenant */}
      {super_admin_visiting_slug && (
        <div className="bg-[#FFC107] text-[#202020] px-4 py-2 text-sm font-medium text-center">
          🔙 Modo Super Admin — viendo {super_admin_visiting_account_name || super_admin_visiting_slug}.{' '}
          <Link
            href="/admin/exit_tenant"
            method="post"
            as="button"
            className="underline font-bold hover:text-[#333]"
          >
            Volver al Panel
          </Link>
        </div>
      )}

      <nav className="bg-[#202020] text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-3">
              <Link href={account ? `/${account.slug}/dashboard` : '/'} className="flex items-center">
                <img
                  src="/esmipos-icon.png"
                  alt="esmiPOS"
                  className="h-8 w-8 rounded"
                />
              </Link>
              {account && (
                <NotificationBell
                  account={account}
                  initialUnreadCount={unread_notifications_count ?? 0}
                />
              )}
            </div>

            <div className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="hover:text-[#FFC107] transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              {account && (
                <Link
                  href="/session"
                  method="delete"
                  as="button"
                  className="bg-[#FFC107] text-[#202020] px-3 py-1 rounded hover:bg-[#e6ad06] transition-colors"
                >
                  Salir
                </Link>
              )}
            </div>

            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#2a2a2a] pb-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block px-4 py-2 hover:bg-[#333] hover:text-[#FFC107]"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            {account && (
              <Link
                href="/session"
                method="delete"
                as="button"
                className="block w-full text-left px-4 py-2 hover:bg-[#333] text-[#FFC107]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Salir
              </Link>
            )}
          </div>
        )}
      </nav>

      <main className={fullWidth ? "w-full py-6 px-4" : "max-w-7xl mx-auto py-6 px-4"}>
        {flash?.notice && (
          <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {flash.alert}
          </div>
        )}
        {children}
      </main>
    </div>
  )
}
